import classNames from 'classnames';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ReactModal from 'react-modal';

import Button from '@/components/Button';
import IconButton from '@/components/Button/IconButton';
import CloseIcon from '@/ui-assets/icons/close-icon.svg?react';
import { onKeyDownHandler } from '@/utils/a11y';
import { trackEvent } from '@/utils/analytics';

import modalStyles from '../../scss/modal.module.scss';

import styles from './Acmodal.module.scss';
import type { ModalProps } from './type';

const AcModal = ({
  className,
  isOpen = false,
  isConfirmLoading = false,
  isCancelLoading = false,
  children,
  cancelText = 'action.cancel',
  confirmText = 'action.confirm',
  confirmTextI18nProps,
  cancelTextI18nProps,
  onConfirm,
  onClose,
}: ModalProps) => {
  const { t } = useTranslation();

  const contentRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => {
    trackEvent(
      'confirm_modal',
      'closed',
      { eventId: 'confirm_modal_closed' },
      { modalType: 'ac' }
    );
    onClose();
  }, [onClose]);

  const handleConfirm = useCallback(() => {
    trackEvent(
      'confirm_modal',
      'clicked',
      { eventId: 'confirm_modal_confirm_button_clicked' },
      { modalType: 'ac' }
    );
    onConfirm?.();
  }, [onConfirm]);

  return (
    <ReactModal
      isOpen={isOpen}
      className={classNames(styles.modal, className)}
      overlayClassName={classNames(modalStyles.overlay, styles.overlay)}
      onAfterOpen={() => {
        contentRef.current?.focus();
      }}
      onRequestClose={handleClose}
    >
      <div
        ref={contentRef}
        className={styles.container}
        role="button"
        tabIndex={0}
        onKeyDown={onKeyDownHandler({
          Enter: onConfirm ? handleConfirm : handleClose,
          ' ': onConfirm ? handleConfirm : handleClose,
          Escape: handleClose,
        })}
      >
        <div style={{display: 'none'}} className={styles.header}>
          {t('description.reminder')}
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </div>
        <div className={styles.content}>{children}</div>
        <div className={styles.footer}>
          {onConfirm && (
            <Button
              title={confirmText}
              i18nProps={confirmTextI18nProps}
              size="small"
              isLoading={isConfirmLoading}
              onClick={handleConfirm}
            />
          )}
          <Button
            title={cancelText}
            type="secondary"
            i18nProps={cancelTextI18nProps}
            size="small"
            isLoading={isCancelLoading}
            onClick={handleClose}
          />
        </div>
      </div>
    </ReactModal>
  );
};

export default AcModal;
